import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Collapse,
  Flex,
  Link,
  ListItem,
  Spinner,
  Stack,
  Text,
  UnorderedList,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import {
  IResourceFile,
  useRequestMoreDetailResources,
} from '../../../../../services/hooks/Resources/useRequestMoreDetailResources';
import { memo, useEffect } from 'react';
import { BiLink } from 'react-icons/bi';
import { useGetImpugnationDetail } from '../../../../../services/hooks/Impugnation/useGetImpugnationDetail';
import TitleSectionMobilePage from '../../../../../components/Mobile/titleSection';
import LabelWithValueMobilePage from '../../../../../components/Mobile/labelWithValue';

interface IOwnProps {
  id: string;
  isOpen: boolean;
  isImpugnation: boolean;
}

interface IMoreDetail {
  observation?: string;
  proof_subject?: string;
  justification?: string;
  ccv_response?: string;
  proof_link?: string;
  status?: string;
  attachments?: IResourceFile[];
}

const MoreDetailMobilePage: React.FC<IOwnProps> = ({ id, isImpugnation, isOpen }) => {
  const seeMore = useDisclosure();
  const impugnation = useGetImpugnationDetail(id, false);
  const resource = useRequestMoreDetailResources(id, false);

  const { isLoading, refetch } = isImpugnation ? impugnation : resource;

  let detailItem: IMoreDetail | undefined = undefined;

  if (isImpugnation && impugnation.data) {
    detailItem = {
      observation: impugnation?.data?.objection,
      justification: impugnation?.data?.selective_process?.no_reply && impugnation?.data?.response_objection,
      status: impugnation?.data?.selective_process?.no_reply && impugnation?.data?.status?.name,
    };
  } else if (resource.data) {
    detailItem = {
      observation: resource.data?.observation,
      proof_subject: resource.data?.proof_subject?.name,
      justification: resource.data?.justification,
      ccv_response: resource.data?.ccv_response,
      proof_link: resource.data?.proof_link,
      status: resource.data?.status?.name,
      attachments: resource.data?.attachments?.resource_file,
    };
  }

  useEffect(() => {
    if (isOpen && !detailItem) {
      refetch();
    }
  }, [isOpen]);

  if (isLoading) {
    return (
      <Stack py={5} align="center">
        <Spinner />
      </Stack>
    );
  }

  const observation = detailItem?.observation || '';

  const showJustification = isImpugnation ? impugnation.data?.selective_process.no_reply : true;

  return (
    <Collapse in={isOpen} animateOpacity>
      <Accordion mt={3} allowMultiple>
        <AccordionItem border="1px solid" borderColor="gray.400" borderRadius="md" overflow="hidden">
          <AccordionButton
            background="white"
            sx={{ WebkitTapHighlightColor: 'transparent' }}
            _hover={{ bg: 'transparent' }}
            color="black"
          >
            <Text fontWeight="semibold" as="span" flex="1" textAlign="left">
              Fundamentação {isImpugnation ? 'da impugnação' : 'do recurso'}
            </Text>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel pb={2} pt={0}>
            {resource?.data?.examination_board && (
              <Stack gap={2}>
                <Stack gap={0}>
                  <Text fontWeight="bold">Banca examinadora: </Text>
                  <Text>{resource?.data?.examination_board.name}</Text>
                </Stack>
                <Stack gap={0}>
                  <Text fontWeight="bold">Unidade acadêmica: </Text>
                  <Text>{resource?.data?.examination_board.academic_unit}</Text>
                </Stack>
                {resource?.data?.examination_board.member && (
                  <Stack gap={0}>
                    <Text fontWeight="bold">Membro da banca:</Text>
                    <Text>{resource?.data?.examination_board.member?.colaborator_data?.name}</Text>
                  </Stack>
                )}
              </Stack>
            )}
            {detailItem?.observation && (
              <Box mt={2}>
                <Text fontWeight={'bold'}>Justificativa:</Text>
                <Text mt="-1px" lineHeight="tall">
                  {(observation.length > 200 && (
                    <>
                      {observation.substring(0, seeMore.isOpen ? observation.length : 200)}
                      {seeMore.isOpen ? '' : '...'}
                      <Text
                        whiteSpace="nowrap"
                        as="span"
                        onClick={seeMore.onToggle}
                        cursor="pointer"
                        fontSize="small"
                        ml={1}
                        color="blue.400"
                      >
                        ver {seeMore.isOpen ? 'menos' : 'mais'}
                      </Text>
                    </>
                  )) ||
                    observation}
                </Text>
              </Box>
            )}
            {detailItem?.attachments && (
              <Stack m={0} p={0} gap={0} mt={4} ml={2} pb={2}>
                <TitleSectionMobilePage>Documentos anexados</TitleSectionMobilePage>
                {!detailItem?.attachments && <Text>Sem Anexos</Text>}
                <UnorderedList p={0} ml={2}>
                  {detailItem?.attachments?.map((attach, index) => (
                    <ListItem listStyleType="''" key={index} p={0} m={0}>
                      <Flex color="colorLink" gap={1} align="center">
                        <Box>
                          <BiLink />
                        </Box>
                        <Link target="_blank" href={attach.url}>
                          <Text fontSize="0.8rem">Anexo {index + 1} </Text>
                        </Link>
                      </Flex>
                    </ListItem>
                  ))}
                </UnorderedList>
              </Stack>
            )}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
      {showJustification && (
        <Box mt={4}>
          <VStack align="start" fontSize="x-small" spacing={0}>
            <TitleSectionMobilePage>Resposta da análise do recurso</TitleSectionMobilePage>
            <Stack gap={0.5} ml={2}>
              <LabelWithValueMobilePage label="Situação" value={detailItem?.status} />

              {detailItem?.proof_link && (
                <Box mt={1}>
                  <Text fontWeight={'bold'} fontSize="x-small">
                    Prova do candidato:
                  </Text>
                  <Link href={detailItem.proof_link} target="_blank" color="blue.400" fontSize="0.75rem" ml={1}>
                    Visualizar prova
                  </Link>
                </Box>
              )}

              {detailItem?.justification && (
                <LabelWithValueMobilePage
                  label="Justificativa da análise"
                  value={detailItem?.justification}
                  acceptTag
                />
              )}

              {detailItem?.ccv_response && (
                <LabelWithValueMobilePage
                  label="Manifestação complementar da CCV"
                  value={detailItem?.ccv_response}
                  acceptTag
                />
              )}
            </Stack>
          </VStack>
        </Box>
      )}
    </Collapse>
  );
};

export default memo(MoreDetailMobilePage);
